/**
 * Řádky destinací karty člověka: u každé destinace role, pořadí, telefon (primární řádek
 * telefon karty, další přebití) a nápověda „U destinace dále: …" (vize F6.1, F6.9).
 *
 * Čistě prezentační: žádný dotaz ani `DestinationPicker` (test běží v `node` bez
 * `QueryClientProvider`). Výběr destinací i dotaz seznamu karet drží stránka. Popisky rolí
 * přicházejí props (`SECTION_LABELS` stránky čte `i18n.t` při importu), pořadí voleb je
 * kanonické pořadí `TEAM_SECTION_TYPES`.
 *
 * Háčky E2E: `data-team-no-destinations`, `data-team-destinations`,
 * `data-team-destination="<id>"`, `data-team-role`, `data-team-sort`, `data-team-phone`,
 * `data-team-others`, `data-team-other="<id>"` s `data-name` a `data-sort`.
 */
import { useTranslation } from "react-i18next";
import { TEAM_SECTION_TYPES, type TeamSectionType } from "@ckgo2/shared/types";
import { parseSortOrder, type TeamFormLink, type TeamOther } from "./teamDestinations";

export interface TeamDestinationRowsProps {
  links: readonly TeamFormLink[];
  destinationName: (id: string) => string;
  /** Mapa `SECTION_LABELS` stránky. */
  roleLabels: Record<TeamSectionType, string>;
  /** Telefon karty, text primárního řádku. */
  cardPhone: string | undefined;
  /** Ostatní karty u destinace; `undefined` = seznam karet se načítá. */
  others: (destinationId: string) => readonly TeamOther[] | undefined;
  onRowChange: (index: number, patch: Partial<TeamFormLink>) => void;
}

export function TeamDestinationRows({
  links,
  destinationName,
  roleLabels,
  cardPhone,
  others,
  onRowChange,
}: TeamDestinationRowsProps) {
  const { t } = useTranslation();

  if (links.length === 0) {
    return (
      <p data-team-no-destinations="" className="text-sm text-gray-500">
        {t("people.edit.noDestinations")}
      </p>
    );
  }

  return (
    <div data-team-destinations="" className="max-h-[32rem] space-y-2 overflow-y-auto pr-1">
      {links.map((link, index) => {
        const rowId = `team-dest-${index}`;
        const rowOthers = others(link.destinationId);
        return (
          <div
            key={link.destinationId}
            data-team-destination={link.destinationId}
            className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700"
          >
            <div className="flex flex-wrap items-end gap-3">
              <span className="min-w-[8rem] flex-1 self-center text-sm font-medium text-gray-800 dark:text-gray-200">
                {destinationName(link.destinationId)}
              </span>
              <div>
                <label htmlFor={`${rowId}-role`} className="label">
                  {t("people.edit.roleAtDestination")}
                </label>
                <select
                  id={`${rowId}-role`}
                  data-team-role=""
                  value={link.role}
                  onChange={(e) => onRowChange(index, { role: e.target.value as TeamSectionType })}
                  className="input w-48"
                >
                  {TEAM_SECTION_TYPES.map((role) => (
                    <option key={role} value={role}>
                      {roleLabels[role]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor={`${rowId}-sort`} className="label">
                  {t("people.edit.sortAtDestination")}
                </label>
                <input
                  id={`${rowId}-sort`}
                  data-team-sort=""
                  type="number"
                  min={0}
                  value={link.sortOrder}
                  onChange={(e) =>
                    onRowChange(index, { sortOrder: parseSortOrder(e.target.value) })
                  }
                  className="input w-28"
                />
              </div>
            </div>
            {index === 0 ? (
              <p className="text-xs text-gray-500">
                {t("catalog.teams.edit.primaryDestination", { phone: cardPhone || "—" })}
              </p>
            ) : (
              <input
                type="text"
                data-team-phone=""
                value={link.phone ?? ""}
                onChange={(e) => onRowChange(index, { phone: e.target.value })}
                className="input w-56"
                placeholder={t("catalog.teams.edit.phoneOverride")}
              />
            )}
            {rowOthers !== undefined && (
              <div data-team-others="" className="text-xs text-gray-500">
                {rowOthers.length === 0 ? (
                  <p>{t("people.edit.othersNone")}</p>
                ) : (
                  <>
                    <p>{t("people.edit.othersLabel")}</p>
                    <ul className="ml-4 list-disc">
                      {rowOthers.map((other) => (
                        <li
                          key={other.id}
                          data-team-other={other.id}
                          data-name={other.name}
                          data-sort={other.sortOrder}
                        >
                          {t("people.edit.otherItem", {
                            name: other.name,
                            role: roleLabels[other.role],
                            order: other.sortOrder,
                          })}
                          {!other.isActive && ` · ${t("people.edit.inactive")}`}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
